import { useSyncExternalStore } from "react";
import {
  reconstructProductModel,
  type ReconstructReason,
  type ReconstructRequest,
} from "./productImport";
import { useProducts } from "./products";
import { useDesign } from "./store";
import type { ProductCapture } from "./types";

/**
 * Background "build a 3D model from the listing photos" jobs, keyed by the
 * import's request id. A job outlives the import sheet: once the sheet
 * saves the product (`linkProductModelJob`), a mesh that finishes later
 * lands on that product and on every piece of it in the current room.
 */
export type ProductModelJob =
  | { status: "building" }
  | { status: "ready"; capture: ProductCapture }
  | { status: "failed"; reason: ReconstructReason };

const jobs = new Map<string, ProductModelJob>();
const productIds = new Map<string, string>();
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

function setJob(requestId: string, job: ProductModelJob) {
  jobs.set(requestId, job);
  notify();
}

/** Start rebuilding a mesh for this import. Once per request id. */
export function startProductModelJob(request: ReconstructRequest): void {
  const { requestId } = request;
  if (jobs.has(requestId)) return;
  setJob(requestId, { status: "building" });
  void reconstructProductModel(request).then((outcome) => {
    if (outcome.capture) {
      setJob(requestId, { status: "ready", capture: outcome.capture });
      deliver(requestId);
    } else {
      setJob(requestId, { status: "failed", reason: outcome.reason });
    }
  });
}

/** The import was saved as `productId`; a mesh that finishes later goes there. */
export function linkProductModelJob(requestId: string, productId: string): void {
  productIds.set(requestId, productId);
  notify();
  deliver(requestId);
}

function deliver(requestId: string) {
  const job = jobs.get(requestId);
  const productId = productIds.get(requestId);
  if (job?.status !== "ready" || !productId) return;
  const products = useProducts.getState();
  const product = products.products.find((p) => p.id === productId);
  if (product && !product.capture) products.setProductCapture(productId, job.capture);
  useDesign.getState().attachProductCapture(productId, job.capture);
}

export function getProductModelJob(requestId: string | undefined): ProductModelJob | null {
  return requestId ? (jobs.get(requestId) ?? null) : null;
}

/** The job feeding a saved product, if any. */
export function getProductModelJobFor(productId: string | undefined): ProductModelJob | null {
  if (!productId) return null;
  for (const [requestId, id] of productIds) {
    if (id === productId) return jobs.get(requestId) ?? null;
  }
  return null;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useProductModelJob(requestId: string | undefined): ProductModelJob | null {
  return useSyncExternalStore(
    subscribe,
    () => getProductModelJob(requestId),
    () => null
  );
}

export function useProductModelJobFor(productId: string | undefined): ProductModelJob | null {
  return useSyncExternalStore(
    subscribe,
    () => getProductModelJobFor(productId),
    () => null
  );
}

/** Tests only. */
export function resetProductModelJobs(): void {
  jobs.clear();
  productIds.clear();
  notify();
}
